import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';
import { Select2Field } from '@/components/forms/Select2Field';
import type { SelectOption } from '@/components/forms/SelectField';

export interface ProviderFormValues {
  name: string;
  type: string;
  searchKeyword: string;
}

const schema = z.object({
  name: z.string().trim().min(1, 'Please enter the provider name.'),
  type: z.string().min(1, 'Please select the provider type.'),
  searchKeyword: z.string(),
});

interface ProviderFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  typeOptions: SelectOption[];
  initial?: ProviderFormValues;
  busy?: boolean;
  onSubmit: (values: ProviderFormValues) => void;
  onClose: () => void;
}

export function ProviderFormModal({
  show,
  mode,
  typeOptions,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: ProviderFormModalProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProviderFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? '',
      type: initial?.type ?? '',
      searchKeyword: initial?.searchKeyword ?? '',
    },
  });

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'Create New' : 'Edit Provider'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="provider-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="provider-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField label="Name" autoComplete="off" error={errors.name?.message} {...register('name')} />
        <TextField
          label="Keyword"
          autoComplete="off"
          error={errors.searchKeyword?.message}
          {...register('searchKeyword')}
        />
        <Select2Field
          control={control}
          name="type"
          label="Type"
          placeholder="Select a type…"
          options={typeOptions}
        />
      </form>
    </Modal>
  );
}
